<script setup>
import Button from "@/components/Button/index.vue";
import CurrencyBuySummary from "@/views/orders/Summary/CurrencyBuy.vue";
import ShopSummary from "@/views/orders/Summary/Shop.vue";
import Summary from "@/views/orders/Summary/Index.vue";
import {useMainOrdersStore} from "@/stores/mainOrders.js";
import {useUserStore} from "@/stores/user.js";
import {computed} from "vue";

const ordersStore = useMainOrdersStore();
const userStore = useUserStore();
const props = defineProps({
  addOrderRoute: String,
  summary: String,
  applyFilter: Function,
  modelValue: String,
  buttonTitle: {
    type: String,
    default: 'New Order'
  },
  showCategoriesButtons: {
    type: Boolean,
    default: true
  },
  showFilter: {
    type: Boolean,
    default: true
  },
  summaryPermission: {
    type: String,
    default: 'BIG_BOSS'
  },
  addOrderPermission: {
    type: String,
    default: 'BIG_BOSS'
  },
  buttonForNewItem: {
    type: Boolean,
    default: false
  },
  titleForNewItem: {
    type: String
  },
  permissionForNewItem: {
    type: String
  },
  copyButton: {
    type: Boolean,
    default: true
  }
})
const emit = defineEmits(['update:modelValue', 'buttonForNewItemClicked', 'copyUUIDs']);

async function filterByFinalized(finalized = false) {
  ordersStore.set_filter('finalized', finalized)
  props.applyFilter();
}

function buttonForNewItemClicked() {
  emit('buttonForNewItemClicked')
}

function copyUUIDs() {
  emit('copyUUIDs')
}

const hasSummaryAccess = computed(() => userStore.hasPermission(props.summaryPermission) || userStore.hasPermission('BIG_BOSS'))
const hasAddOrderAccess = computed(() => userStore.hasPermission(props.addOrderPermission) || userStore.hasPermission('BIG_BOSS'))
const hasNewItemAccess = computed(() => userStore.hasPermission(props.permissionForNewItem) || userStore.hasPermission('BIG_BOSS'))
</script>

<template>
  <div class="flex-1 flex flex-row gap-3 justify-end">
    <div
        class="flex flex-row gap-2 items-center w-max h-[32px] bg-slate-900 border border-slate-700 text-slate-300 px-2 rounded-md">
      <span>
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24">
          <path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                d="m21 21l-4.343-4.343m0 0A8 8 0 1 0 5.343 5.343a8 8 0 0 0 11.314 11.314"/>
        </svg>
      </span>
      <input :value="modelValue"
             @input="$emit('update:modelValue', $event.target.value)"
             type="text"
             class="bg-transparent w-full block focus:outline-none h-full rounded-md text-sm">
      <button v-if="modelValue"
              @click="$emit('update:modelValue', '')" class="hover:text-red-400">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16">
          <path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                d="m11.25 4.75l-6.5 6.5m0-6.5l6.5 6.5"/>
        </svg>
      </button>
    </div>
    <button v-if="showFilter" @click="ordersStore.show_filter_modal()"
            class="btn btn-outline-dark py-1 text-xs font-medium rounded-md h-[32px]"
            :class="{'border !border-blue-500 shadow-[0_0_20px_5px_rgba(59,130,246,0.6)]': ordersStore.isFilterActive}">
      Filters {{ ordersStore.isFilterActive ? '(' + ordersStore.activeFilterCount + ')' : '' }}
    </button>
    <!--    <component :is="summary" />-->
    <Summary v-if="summary === 'BoostSummary' && hasSummaryAccess"
             type="boost"
             orderType="Boost"
             summeryType="Boost"/>
    <Summary v-if="summary === 'CurrencySellSummary' && hasSummaryAccess"
             type="currencySell"
             orderType="Currency"
             summeryType="CurrencySell"/>
    <Summary v-if="summary === 'AccountSummary' && hasSummaryAccess"
             type="account"
             orderType="Account"
             summeryType="Account"/>
    <Summary v-if="summary === 'GiftCardSummary' && hasSummaryAccess"
             type="giftCard"
             orderType="Gift Card"
             summeryType="GiftCard"/>
    <Summary v-if="summary === 'ItemSummary' && hasSummaryAccess"
             type="item"
             orderType="Item"
             summeryType="Item"/>
    <CurrencyBuySummary v-if="summary === 'CurrencyBuySummary' && hasSummaryAccess"/>
    <ShopSummary v-if="summary === 'ShopSummary' && hasSummaryAccess"/>
    <div v-if="showCategoriesButtons"
         class="w-max border border-slate-200 dark:border-slate-700 dark:bg-slate-900 flex items-center rounded-md text-xs h-[32px]">
        <span
            class="flex-1 font-normal px-3 py-1 rounded-r ltr:rounded-l ltr:rounded-r-none transition-all duration-150 cursor-pointer h-full items-center flex"
            :class="
                  ordersStore.filters.finalized === false
                    ? 'bg-slate-900 text-white dark:bg-slate-700 dark:text-slate-300'
                    : 'dark:text-slate-300'
                "
            @click="filterByFinalized(false)"
        >Delivering</span>
      <span
          class="flex-1 font-normal px-3 py-1 transition-all duration-150 cursor-pointer h-full items-center flex"
          :class="
          ordersStore.filters.finalized === true
            ? 'bg-slate-900 text-white dark:bg-slate-700 dark:text-slate-300'
            : 'dark:text-slate-300'
        "
          @click="filterByFinalized(true)"
      >Archived</span>
      <span
          class="flex-1 font-normal px-3 py-1 rounded-l ltr:rounded-r ltr:rounded-l-none transition-all duration-150 cursor-pointer h-full items-center flex"
          :class="
                  ordersStore.filters.finalized === null
                    ? 'bg-slate-900 text-white dark:bg-slate-700 dark:text-slate-300'
                    : 'dark:text-slate-300'
                "
          @click="filterByFinalized(null)"
      >All</span>
    </div>
    <router-link v-if="addOrderRoute && hasAddOrderAccess" :to="{name: addOrderRoute}"
                 class="btn btn-outline-dark py-1 text-xs font-medium rounded-md flex items-center h-[32px]">
      {{ buttonTitle }}
    </router-link>
    <button v-if="buttonForNewItem && hasNewItemAccess"
            class="btn btn-outline-dark py-1 text-xs font-medium rounded-md whitespace-nowrap h-[32px]"
            @click="buttonForNewItemClicked">{{ titleForNewItem }}
    </button>
    <button v-if="userStore.hasPermission('BIG_BOSS') && copyButton"
            @click="copyUUIDs" class="btn btn-outline-dark p-1 text-white h-[32px]">
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 512 512">
        <rect width="336" height="336" x="128" y="128" fill="none" stroke="currentColor" stroke-linejoin="round"
              stroke-width="32" rx="57" ry="57"/>
        <path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="32"
              d="m383.5 128l.5-24a56.16 56.16 0 0 0-56-56H112a64.19 64.19 0 0 0-64 64v216a56.16 56.16 0 0 0 56 56h24"/>
      </svg>
    </button>
  </div>
</template>

<style scoped lang="scss">

</style>